import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router, UrlMatcher, UrlSegment } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { VacancyDocumentService } from './services/vacancy-document.service';
import { VacancyResolution } from './vacancy-document';

const JOB_SEGMENT = /^(?:[a-z0-9]+(?:-[a-z0-9]+)*-)?[0-9]{1,10}$/;

/** `/vacatures/<slug>-<id>`, the legacy `/vacatures/<id>` and `/vacatures/bekijk-vacature/<id>`. */
export const vacancyJobMatcher: UrlMatcher = (segments: UrlSegment[]) => {
  if (segments[0]?.path !== 'vacatures') return null;
  if (segments.length === 2 && JOB_SEGMENT.test(segments[1].path)) return { consumed: segments };
  if (segments.length === 3 && segments[1].path === 'bekijk-vacature' && /^[0-9]{1,10}$/.test(segments[2].path)) return { consumed: segments };
  return null;
};

/** Breadcrumb leaf of a vacancy page, derived from its resolved document. */
export function vacancyBreadcrumb(data: Record<string, unknown>): string | null {
  const document = data['document'] as VacancyResolution | undefined;
  switch (document?.kind) {
    case 'job':
      return document.job.title;
    case 'gone':
      return 'Vacature niet meer beschikbaar';
    case 'not-found':
      return 'Vacature niet gevonden';
    default:
      return null;
  }
}

/**
 * One resolution per navigation. A redirect (legacy URL or outdated slug) replaces the URL with the
 * canonical one before anything renders, keeping the query; everything else renders as a state.
 */
export const vacancyDocumentResolver: ResolveFn<VacancyResolution> = (route, state) => {
  const router = inject(Router);
  const path = state.url.split(/[?#]/)[0];
  const outcome$: Observable<VacancyResolution | RedirectCommand> = inject(VacancyDocumentService)
    .resolve(path)
    .pipe(
      map((resolution) => {
        if (resolution.kind !== 'redirect') return resolution;
        const target = router.parseUrl(resolution.path);
        target.queryParams = { ...route.queryParams };
        return new RedirectCommand(target, { replaceUrl: true });
      }),
    );
  return outcome$;
};
